import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
  VStack,
  HStack,
  Image,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
  Spinner,
  List,
  ListItem,
  Badge,
  chakra,
  Center,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure
} from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import supabase from '../utils/supabaseUtils';
import StripeWrapper from './StripeWrapper';
import StripePaymentForm from './StripePaymentForm';
import { saveOrder } from '../utils/stripeUtils';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { FaArrowLeft, FaTimes } from 'react-icons/fa';
import logo from '../assets/playmaker-logo.png';

const CheckoutPage = () => {
  const [paymentMethod, setPaymentMethod] = useState('credit_card');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { user } = useAuth();
  const { cartItems, cartTotal, cartPackage, cartRequiresShipping } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  // Shipping options
  const shippingOptions = [
    { id: 'digital', name: 'Digital Only', price: 0, description: 'Instant download, no shipping required' },
    { id: 'standard', name: 'Standard Shipping', price: 5.99, description: '5-7 business days' },
    { id: 'express', name: 'Express Shipping', price: 15.99, description: '2-3 business days' }
  ];
  const [selectedShipping, setSelectedShipping] = useState('digital');
  
  // Local cart state (used when context is not available)
  const [localCartItems, setLocalCartItems] = useState([]);
  const [localCartPackage, setLocalCartPackage] = useState(null);
  const [localCartTotal, setLocalCartTotal] = useState(0);
  const [localRequiresShipping, setLocalRequiresShipping] = useState(false);
  
  // Frame package extras
  const [selectedFrameImageId, setSelectedFrameImageId] = useState(null);
  const [frameColor, setFrameColor] = useState('black');
  const {
    isOpen: isFrameInfoOpen,
    onOpen: onFrameInfoOpen,
    onClose: onFrameInfoClose
  } = useDisclosure();
  
  // Load cart data 
  useEffect(() => {
    console.log('CheckoutPage: Loading cart data', {
      contextCartItems: cartItems,
      contextCartTotal: cartTotal,
      contextCartPackage: cartPackage
    });
    
    // If cart data is already available from context, use it
    if (cartItems && cartItems.length > 0) {
      console.log('Using cart data from context:', { cartItems, cartTotal, cartPackage });
      setSelectedShipping(cartRequiresShipping ? 'standard' : 'digital');
      return;
    }
    
    const loadCartData = async () => {
      setLoading(true);
      try {
        // First, try to get cart data from localStorage
        const storedCartData = localStorage.getItem('playmaker_cart');
        
        if (storedCartData) {
          const parsedCart = JSON.parse(storedCartData);
          console.log('Retrieved cart data from localStorage:', parsedCart);
          
          // Upgrade Basic package price if outdated
          if (parsedCart.package && parsedCart.package.id === 'basic' && parsedCart.package.price && parsedCart.package.price < 30) {
            console.log('CheckoutPage: Upgrading Basic package price from', parsedCart.package.price, 'to 30');
            parsedCart.package.price = 30;
            parsedCart.total = 30;
          }
          
          if (parsedCart.items && parsedCart.items.length > 0) {
            // Set local state for checkout page rendering
            setLocalCartItems(parsedCart.items);
            setLocalCartPackage(parsedCart.package);
            setLocalCartTotal(parsedCart.total);
            setLocalRequiresShipping(parsedCart.requires_shipping || false);
            setSelectedShipping(parsedCart.requires_shipping ? 'standard' : 'digital');
            
            console.log('Successfully loaded cart data from localStorage', {
              items: parsedCart.items.length,
              package: parsedCart.package?.name,
              total: parsedCart.total
            });
            
            setLoading(false);
            return;
          }
        } else {
          console.log('No playmaker_cart data found in localStorage');
        }
        
        // Fall back to URL parameters if localStorage data isn't available
        const params = new URLSearchParams(location.search);
        const itemCount = parseInt(params.get('items') || '0', 10);
        const packageId = params.get('package');
        const total = parseFloat(params.get('total') || '0');
        const itemIds = params.get('item_ids')?.split(',') || [];
        
        console.log('URL parameters:', { itemCount, packageId, total, itemIds });
        
        if (!itemIds.length || !packageId) {
          console.warn('No valid cart data found in URL parameters');
          setLoading(false);
          return;
        }
        
        try {
          console.log('Attempting to load cart from URL parameters:', {
            itemCount, packageId, total, itemIds
          });
          
          // Package definitions
          const packageOptions = [
            { id: 'basic', name: 'Basic', price: 30, imageCount: 1, description: '1 digital image' },
            { id: 'premium', name: 'Premium', price: 50, imageCount: 2, description: '2 digital images' },
            { id: 'ultimate', name: 'Ultimate', price: 65, imageCount: 3, description: '3 digital images' },
            { id: 'frame', name: 'Frame Package', price: 100, imageCount: 3, description: '3 digital images + 11" x 17" framed print', includedFrames: 1, requires_shipping: true }
          ];
          
          const selectedPackage = packageOptions.find(pkg => pkg.id === packageId) || 
            { id: 'custom', name: 'Custom', price: total, imageCount: itemCount, description: `${itemCount} digital images` };
          
          const needsShipping = selectedPackage.id === 'frame' || selectedPackage.requires_shipping;
          setSelectedShipping(needsShipping ? 'standard' : 'digital');
          
          // Fetch items if we have IDs
          let cartItems = [];
          
          // First try to get from exports table
          let { data: exportData, error: exportError } = await supabase
            .from('exports')
            .select('id, template_id, export_url, export_url_wm, template_name, is_purchased')
            .in('id', itemIds)
            .eq('status', 'active');
          
          if (exportError) {
            console.warn("Error fetching from exports, trying templates:", exportError);
          }
          
          if (exportData && exportData.length > 0) {
            // Format export data
            cartItems = exportData.map(item => ({
              id: item.id,
              name: item.template_name || 'Custom Design',
              url: item.export_url,
              display_url: item.export_url,
              watermarkedUrl: item.export_url_wm || item.export_url,
              templateId: item.template_id,
              is_purchased: item.is_purchased
            }));
          } else {
            // Try templates as fallback
            const { data: templateData, error: templateError } = await supabase
              .from('templates')
              .select('*')
              .in('id', itemIds);
              
            if (templateError) {
              console.error("Error fetching templates:", templateError);
              throw templateError;
            }
            
            if (templateData && templateData.length > 0) {
              // Format template data to match expected structure
              cartItems = templateData.map(item => ({
                id: item.id,
                name: item.name || 'Custom Design',
                url: item.display_url || item.url,
                display_url: item.display_url || item.url,
                watermarkedUrl: item.wm_url || item.url,
                templateId: item.id
              }));
            }
          }
          
          // If we have cart items, update state and localStorage
          if (cartItems.length > 0) {
            console.log(`Successfully loaded ${cartItems.length} cart items`);
            
            // Save to local state
            setLocalCartItems(cartItems);
            setLocalCartPackage(selectedPackage);
            setLocalCartTotal(selectedPackage.price);
            setLocalRequiresShipping(needsShipping);
            
            // Store in localStorage for future reference
            const cartData = {
              items: cartItems,
              package: selectedPackage,
              total: selectedPackage.price,
              requires_shipping: needsShipping,
              timestamp: Date.now()
            };
            
            localStorage.setItem('playmaker_cart', JSON.stringify(cartData));
            console.log('Cart data saved to localStorage:', cartData);
          } else {
            console.error('Failed to load any cart items for the IDs:', itemIds);
            throw new Error('No cart items found');
          }
        } catch (error) {
          console.error('Error loading cart from URL parameters:', error);
          toast({
            title: 'Error Loading Cart',
            description: 'Could not load your cart items. Please try adding items to your cart again.',
            status: 'error',
            duration: 5000,
            isClosable: true
          });
        }
      } catch (error) {
        console.error("Error loading cart data:", error);
        toast({
          title: "Error loading cart",
          description: "There was a problem loading your cart items. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadCartData();
  }, [location.search, cartItems, cartTotal, cartPackage, cartRequiresShipping, toast]);
  
  // Determine which cart data to use (context or local)
  const effectiveCartItems = cartItems?.length > 0 ? cartItems : localCartItems;
  const effectiveCartPackage = cartPackage || localCartPackage;
  const effectiveCartTotal = cartTotal || localCartTotal;
  const effectiveRequiresShipping = cartRequiresShipping || localRequiresShipping;
  
  // Calculate shipping cost
  const getShippingCost = () => {
    const option = shippingOptions.find(opt => opt.id === selectedShipping);
    return option ? option.price : 0;
  };
  
  // Calculate totals
  const subtotal = effectiveCartTotal || 0;
  const shippingCost = effectiveRequiresShipping ? getShippingCost() : 0;
  const tax = subtotal * 0.08; // 8% tax rate
  const total = subtotal + shippingCost + tax;
  
  // NEW: 50% discount calculations
  const discountFactor = 0.5;
  const discountedSubtotal = subtotal * discountFactor;
  const discountedShippingCost = shippingCost * discountFactor;
  const discountedTax = (discountedSubtotal + discountedShippingCost) * 0.08;
  const discountedTotal = discountedSubtotal + discountedShippingCost + discountedTax;
  
  // Ensure a default selection for frame image once items are known.
  // This hook must be declared before any early return to avoid violating
  // the Rules of Hooks (which caused React error #310).
  useEffect(() => {
    if (
      effectiveCartPackage?.id === 'frame' &&
      effectiveCartItems.length > 0 &&
      !selectedFrameImageId
    ) {
      setSelectedFrameImageId(effectiveCartItems[0].id);
    }
  }, [effectiveCartPackage, effectiveCartItems, selectedFrameImageId]);
  
  // Convert to cents for Stripe (use discounted total)
  const amountInCents = Math.round(discountedTotal * 100);
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is modified
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  // Validate form fields
  const validateForm = () => {
    const newErrors = {};
    
    // Only validate shipping info if shipping is required
    if (effectiveRequiresShipping && selectedShipping !== 'digital') {
      if (!formData.fullName.trim()) newErrors.fullName = 'Name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
      if (!formData.address.trim()) newErrors.address = 'Address is required';
      if (!formData.city.trim()) newErrors.city = 'City is required';
      if (!formData.state.trim()) newErrors.state = 'State is required';
      if (!formData.zip.trim()) {
        newErrors.zip = 'ZIP code is required';
      } else if (!/^\d{5}(-\d{4})?$/.test(formData.zip)) {
        newErrors.zip = 'Invalid ZIP format';
      }
    } else {
      // For digital purchases, we only need email
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      setLoading(true);
      console.log('Payment successful! Processing order...');
      
      // Get the item IDs from the cart
      const itemIds = effectiveCartItems.map(item => item.id).filter(Boolean);
      
      // Extract event_id from cart items or stored cart data
      const storedCart = JSON.parse(localStorage.getItem('playmaker_cart') || '{}');
      const eventId = effectiveCartItems.length > 0 ? effectiveCartItems[0].event_id : storedCart.event_id;
      
      // Create complete order data
      const orderData = {
        id: paymentIntent.id,
        payment_intent_id: paymentIntent.id,
        amount: discountedTotal,
        items: effectiveCartItems,
        package: effectiveCartPackage,
        itemIds: itemIds,
        status: paymentIntent.status,
        frame_color: frameColor,
        frame_image_id: selectedFrameImageId,
        event_id: eventId,
        image_count: itemIds.length,
        timestamp: new Date().toISOString()
      };
      
      // Log order data
      console.log('Order processed successfully:', {
        paymentIntentId: paymentIntent.id,
        amount: discountedTotal,
        items: itemIds.length
      });
      
      // Store order data in localStorage as a fallback
      localStorage.setItem('last_order_data', JSON.stringify(orderData));
      
      // Clear cart data
      localStorage.removeItem('playmaker_cart');
      
      // Navigate to success page with state AND search params
      navigate('/order-success', { 
        state: { orderData },  // Pass data via state
        search: `?order_success=true&items=${itemIds.join(',')}`  // And via URL params
      });
    } catch (error) {
      console.error('Order processing error:', error);
      toast({
        title: 'Error processing order',
        description: error.message || 'There was an error processing your order',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    toast({
      title: 'Payment failed',
      description: error.message || 'Your payment could not be processed',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: 'Form error',
        description: 'Please check the form for errors',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    // Form is valid, payment is handled by the Stripe component
    // Scroll to payment section
    document.getElementById('payment-section')?.scrollIntoView({ behavior: 'smooth' });
  };
  
  // Add style for close button
  const closeButtonStyle = {
    position: 'fixed',
    top: '20px',
    right: '20px',
    zIndex: 10,
    backgroundColor: 'white',
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
    borderRadius: '50%',
  };
  
  // Style for the new back button (top-left corner)
  const backButtonStyle = {
    position: 'fixed',
    top: '20px',
    left: '20px',
    zIndex: 10,
    backgroundColor: 'white',
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
    borderRadius: '50%',
  };
  
  // Function to handle close button click
  const handleClose = () => {
    // Show confirmation if there are changes or form entries
    if (formData.email || formData.fullName) {
      if (window.confirm('Are you sure you want to leave? Your checkout progress will be lost.')) {
        navigate('/designs');
      }
    } else {
      navigate('/designs');
    }
  };
  
  // Show loading state
  if (loading && !effectiveCartItems.length) {
    return (
      <Container maxW="container.lg" py={10} centerContent>
        <VStack spacing={4}>
          <Spinner size="xl" />
          <Text>Loading your order...</Text>
        </VStack>
      </Container>
    );
  }
  
  // Show error if no cart data is available
  if (!effectiveCartItems || !effectiveCartItems.length) {
    return (
      <Container maxW="container.md" py={16}>
        <IconButton
          icon={<FaTimes />}
          aria-label="Close checkout"
          size="lg"
          colorScheme="gray"
          onClick={handleClose}
          style={closeButtonStyle}
          _hover={{ 
            transform: 'scale(1.1)',
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
          }}
          _active={{
            transform: 'scale(0.95)'
          }}
          transition="all 0.2s"
        />
        <Alert
          status="warning"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          height="200px"
          borderRadius="md"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mb={2}>No Items to Checkout</AlertTitle>
          <AlertDescription maxWidth="sm">
            Please return to "My Designs" in your dashboard and add some designs to your cart before proceeding to checkout.
          </AlertDescription>
        </Alert>
        <Center mt={6}>
          <Button
            leftIcon={<FaArrowLeft />}
            colorScheme="blue"
            onClick={() => navigate('/designs')}
          >
            Go to My Designs
          </Button>
        </Center>
      </Container>
    );
  }
  
  return (
    <>
    <Container maxW="container.lg" py={8}>
      {/* Back button */}
      <IconButton
        icon={<FaArrowLeft color="gray" />}
        aria-label="Go back"
        size="lg"
        colorScheme="gray"
        onClick={() => navigate(-1)}
        style={backButtonStyle}
        _hover={{
          transform: 'scale(1.1)',
          boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
        }}
        _active={{
          transform: 'scale(0.95)'
        }}
        transition="all 0.2s"
      />
      
      {/* Close button */}
      <IconButton
        icon={<FaTimes />}
        aria-label="Close checkout"
        size="lg"
        colorScheme="gray"
        onClick={handleClose}
        style={closeButtonStyle}
        _hover={{ 
          transform: 'scale(1.1)',
          boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
        }}
        _active={{
          transform: 'scale(0.95)'
        }}
        transition="all 0.2s"
      />
      
      {/* Logo */}
      <Flex justify="center" mb={6}>
        <Image src={logo} alt="PlayMaker Pics Logo" height="60px" objectFit="contain" />
      </Flex>
      
      <Box mb={8} textAlign="center">
        <Heading size="xl">Checkout</Heading>
        <Text mt={2} color="gray.600">
          Complete your order to receive your digital templates{effectiveRequiresShipping ? ' and physical items' : ''}
        </Text>
      </Box>
      
      <Grid templateColumns={{ base: "1fr", lg: "3fr 2fr" }} gap={8}>
        {/* Order Form */}
        <GridItem>
          <VStack spacing={8} align="stretch">
            {/* Contact Information */}
            <Box p={6} borderWidth="1px" borderRadius="md" bg={bgColor}>
              <Heading size="md" mb={4}>Contact Information</Heading>
              
              <FormControl isRequired isInvalid={!!errors.email} mb={4}>
                <FormLabel>Email</FormLabel>
                <Input 
                  type="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleInputChange}
                  placeholder="your.email@example.com"
                />
                {errors.email && <Text color="red.500" fontSize="sm">{errors.email}</Text>}
              </FormControl>
            </Box>
            
            {/* Shipping Information - only if physical products are included */}
            {effectiveRequiresShipping && selectedShipping !== 'digital' && (
              <Box p={6} borderWidth="1px" borderRadius="md" bg={bgColor}>
                <Heading size="md" mb={4}>Shipping Information</Heading>
                
                <FormControl isRequired isInvalid={!!errors.fullName} mb={4}>
                  <FormLabel>Full Name</FormLabel>
                  <Input 
                    name="fullName" 
                    value={formData.fullName} 
                    onChange={handleInputChange}
                    placeholder="John Doe"
                  />
                  {errors.fullName && <Text color="red.500" fontSize="sm">{errors.fullName}</Text>}
                </FormControl>
                
                <FormControl isRequired isInvalid={!!errors.address} mb={4}>
                  <FormLabel>Street Address</FormLabel>
                  <Input 
                    name="address" 
                    value={formData.address} 
                    onChange={handleInputChange}
                    placeholder="123 Main St."
                  />
                  {errors.address && <Text color="red.500" fontSize="sm">{errors.address}</Text>}
                </FormControl>
                
                <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
                  <FormControl isRequired isInvalid={!!errors.city}>
                    <FormLabel>City</FormLabel>
                    <Input 
                      name="city" 
                      value={formData.city} 
                      onChange={handleInputChange}
                      placeholder="City"
                    />
                    {errors.city && <Text color="red.500" fontSize="sm">{errors.city}</Text>}
                  </FormControl>
                  
                  <FormControl isRequired isInvalid={!!errors.state}>
                    <FormLabel>State</FormLabel>
                    <Input 
                      name="state" 
                      value={formData.state} 
                      onChange={handleInputChange}
                      placeholder="State"
                    />
                    {errors.state && <Text color="red.500" fontSize="sm">{errors.state}</Text>}
                  </FormControl>
                </Grid>
                
                <FormControl isRequired isInvalid={!!errors.zip} mt={4}>
                  <FormLabel>ZIP Code</FormLabel>
                  <Input 
                    name="zip" 
                    value={formData.zip} 
                    onChange={handleInputChange}
                    placeholder="12345"
                  />
                  {errors.zip && <Text color="red.500" fontSize="sm">{errors.zip}</Text>}
                </FormControl>
              </Box>
            )}
            
            {/* Shipping Options - only if physical products are included */}
            {effectiveRequiresShipping && (
              <Box p={6} borderWidth="1px" borderRadius="md" bg={bgColor}>
                <Heading size="md" mb={4}>Shipping Method</Heading>
                
                <RadioGroup onChange={setSelectedShipping} value={selectedShipping}>
                  <Stack spacing={3}>
                    {shippingOptions.filter(option => option.id !== 'digital' || !effectiveRequiresShipping).map(option => (
                      <Box 
                        key={option.id}
                        p={3}
                        borderWidth="1px"
                        borderRadius="md"
                        borderColor={selectedShipping === option.id ? 'blue.500' : borderColor}
                        _hover={{ borderColor: 'blue.300' }}
                      >
                        <Radio value={option.id} size="lg">
                          <HStack justify="space-between" width="100%">
                            <Box>
                              <Text fontWeight="medium">{option.name}</Text>
                              <Text fontSize="sm" color="gray.500">{option.description}</Text>
                            </Box>
                            {option.price === 0 ? (
                              <Text fontWeight="bold">Free</Text>
                            ) : (
                              <VStack spacing={0} align="flex-end">
                                <Text as="s" color="gray.500" fontWeight="normal">{`$${option.price.toFixed(2)}`}</Text>
                                <Text fontWeight="bold">{`$${(option.price * discountFactor).toFixed(2)}`}</Text>
                              </VStack>
                            )}
                          </HStack>
                        </Radio>
                      </Box>
                    ))}
                  </Stack>
                </RadioGroup>
              </Box>
            )}
            
            {/* Frame Options */}
            {effectiveCartPackage && effectiveCartPackage.id === 'frame' && (
              <Box p={6} borderWidth="1px" borderRadius="md" bg={bgColor}>
                <Heading size="md" mb={4}>Frame Options</Heading>
                <Text fontSize="sm" mb={2}>Select which design you would like printed:</Text>
                <RadioGroup value={selectedFrameImageId} onChange={setSelectedFrameImageId} mb={4}>
                  <Stack spacing={3}>
                    {effectiveCartItems.map(item => (
                      <Radio key={item.id} value={item.id}>
                        <HStack>
                          <Image 
                            src={
                              item.url ||
                              item.display_url ||
                              item.export_url ||
                              item.watermarkedUrl ||
                              item.export_url_wm ||
                              item.watermarked_url
                            }
                            alt={item.name}
                            boxSize="60px"
                            objectFit="cover"
                            borderRadius="md"
                          />
                          <Text>{item.name}</Text>
                        </HStack>
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
                <Divider my={2} />
                <Text fontSize="sm" mb={2}>Choose frame color:</Text>
                <RadioGroup value={frameColor} onChange={setFrameColor}>
                  <Stack direction="row">
                    <Radio value="black">Black</Radio>
                    <Radio value="white">White</Radio>
                  </Stack>
                </RadioGroup>
                <Button variant="link" size="sm" mt={3} onClick={onFrameInfoOpen}>Learn more about frames</Button>
              </Box>
            )}
            
            {/* Payment Section */}
            <Box id="payment-section" p={6} borderWidth="1px" borderRadius="md" bg={bgColor}>
              <Heading size="md" mb={4}>Payment</Heading>
              
              <RadioGroup 
                onChange={setPaymentMethod}
                value={paymentMethod}
                mb={4}
              >
                <Stack direction="row" spacing={5}>
                  <Radio value="credit_card">Credit Card</Radio>
                  <Radio value="paypal" isDisabled>PayPal (Coming Soon)</Radio>
                </Stack>
              </RadioGroup>
              
              {paymentMethod === 'credit_card' && (
                <Box mt={4}>
                  <StripeWrapper>
                    <StripePaymentForm 
                      amount={amountInCents}
                      onPaymentSuccess={handlePaymentSuccess}
                      onPaymentError={handlePaymentError}
                      cardName={formData.fullName || formData.email}
                    />
                  </StripeWrapper>
                </Box>
              )}
            </Box>
          </VStack>
        </GridItem>
        
        {/* Order Summary */}
        <GridItem>
          <Box 
            position="sticky"
            top="20px"
            p={6} 
            borderWidth="1px" 
            borderRadius="md" 
            bg={bgColor}
          >
            <Heading size="md" mb={4}>Order Summary</Heading>
            
            <List spacing={3} mb={6}>
              {effectiveCartItems.map((item, index) => (
                <ListItem key={item.id || index}>
                  <HStack>
                    <Box 
                      borderRadius="md" 
                      overflow="hidden" 
                      width="60px" 
                      height="40px"
                    >
                      <Image 
                        src={
                          item.url ||
                          item.display_url ||
                          item.export_url ||
                          item.watermarkedUrl ||
                          item.export_url_wm ||
                          item.watermarked_url
                        }
                        alt={item.name}
                        fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='40' viewBox='0 0 60 40'%3E%3Crect width='60' height='40' fill='%23eaeaea'/%3E%3Ctext x='30' y='20' font-family='Arial' font-size='6' fill='%23888' text-anchor='middle' dominant-baseline='middle'%3EImage%3C/text%3E%3C/svg%3E"
                        objectFit="cover"
                        width="100%"
                        height="100%"
                      />
                    </Box>
                    <Text flex="1" fontWeight="medium">{item.name || `Template ${index+1}`}</Text>
                  </HStack>
                </ListItem>
              ))}
              
              {/* Add frame item if it's a frame package */}
              {effectiveRequiresShipping && effectiveCartPackage.id === 'frame' && (
                <ListItem>
                  <HStack>
                    <Box 
                      borderRadius="md" 
                      overflow="hidden" 
                      width="60px" 
                      height="40px"
                      bg="gray.100"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text fontSize="xs">Frame</Text>
                    </Box>
                    <Text flex="1" fontWeight="medium">14" x 18" Framed Print</Text>
                  </HStack>
                </ListItem>
              )}
            </List>
            
            <Box p={4} bg="gray.50" borderRadius="md" mb={6}>
              <HStack justify="space-between" mb={2}>
                <Text>Package:</Text>
                <Text fontWeight="medium">{effectiveCartPackage.name}</Text>
              </HStack>
              <HStack justify="space-between" mb={2}>
                <Text>Items:</Text>
                <Text>{effectiveCartItems.length}</Text>
              </HStack>
              {effectiveRequiresShipping && effectiveCartPackage.id === 'frame' && (
                <HStack justify="space-between" mb={2}>
                  <Text>Includes:</Text>
                  <Text>Framed Print</Text>
                </HStack>
              )}
            </Box>
            
            <Divider mb={4} />
            
            <VStack spacing={2} align="stretch">
              <HStack justify="space-between">
                <Text>Subtotal:</Text>
                <HStack>
                  <Text as="s" color="gray.500" mr={1}>${subtotal.toFixed(2)}</Text>
                  <Text>${discountedSubtotal.toFixed(2)}</Text>
                </HStack>
              </HStack>
              
              <HStack justify="space-between">
                <Text>Shipping:</Text>
                {effectiveRequiresShipping ? (
                  <HStack>
                    <Text as="s" color="gray.500" mr={1}>${shippingCost.toFixed(2)}</Text>
                    <Text>${discountedShippingCost.toFixed(2)}</Text>
                  </HStack>
                ) : <Text>Free</Text>}
              </HStack>
              
              <HStack justify="space-between">
                <Text>Tax (8%):</Text>
                <HStack>
                  <Text as="s" color="gray.500" mr={1}>${tax.toFixed(2)}</Text>
                  <Text>${discountedTax.toFixed(2)}</Text>
                </HStack>
              </HStack>
              
              <Divider my={2} />
              
              <HStack justify="space-between" fontWeight="bold">
                <Text>Total:</Text>
                <HStack align="baseline">
                  <Text as="s" color="gray.500" mr={1}>${total.toFixed(2)}</Text>
                  <Text fontSize="xl">${discountedTotal.toFixed(2)}</Text>
                </HStack>
              </HStack>
            </VStack>
            
            <Alert status="info" mt={6} borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                All transactions are secure and encrypted. By completing your order, 
                you agree to our Terms of Service and Privacy Policy.
              </Text>
            </Alert>
          </Box>
        </GridItem>
      </Grid>
    </Container>
    {/* Frame info modal */}
    <Modal isOpen={isFrameInfoOpen} onClose={onFrameInfoClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Frame Options</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <Text>Our high-quality 11&quot; × 17&quot; frame is available in two classic finishes.</Text>
            <Image src="/images/Black Frame 14x18.jpeg" alt="Black Frame" borderRadius="md" />
            <Image src="/images/White Frame 14x18.jpeg" alt="White Frame" borderRadius="md" />
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
    </>
  );
};

export default CheckoutPage; 